import { useWalletHistory } from '@/hooks/useWalletHistory'
import type { WalletTransaction } from '@/lib/api/wallet'
import { formatUSD } from '@/lib/format'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const labels: Record<WalletTransaction['type'], string> = {
  TOP_UP: 'Credit top-up',
  SUBSCRIPTION_CREDIT: 'Monthly top-up',
  JOB_DEDUCTION: 'AI generation',
  REFUND: 'Refund',
  TRAINING_HOLD: 'Model training (held)',
  TRAINING_CONFIRMED: 'Model training',
  TRAINING_REFUND: 'Model training refund',
  ADMIN_DEBIT: 'Adjustment by Genie',
  REFERRAL_CREDIT: 'Invite earnings',
}

/** Ledger rows that take credits out of the wallet. */
const debitTypes: ReadonlySet<WalletTransaction['type']> = new Set(['JOB_DEDUCTION', 'TRAINING_HOLD', 'ADMIN_DEBIT'])

export function BillingTransactionList() {
  const { transactions, isLoading, error, hasMore, refetch, loadMore } = useWalletHistory()

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Credit purchases, generation usage, and refunds.
      </p>
      {error && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border p-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button size="sm" variant="outline" onClick={refetch}>
            Retry transactions
          </Button>
        </div>
      )}
      {transactions.length === 0 ? (
        <p className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
          {isLoading
            ? 'Loading transactions…'
            : error
              ? 'Transactions unavailable'
              : 'No transactions yet'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {new Date(transaction.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>{transaction.description || labels[transaction.type]}</TableCell>
                  <TableCell className="whitespace-nowrap text-right tabular-nums">
                    {debitTypes.has(transaction.type) ? '−' : '+'}
                    {formatUSD(Math.abs(Number(transaction.amount)))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatUSD(Number(transaction.balanceAfter))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {hasMore && (
        <Button variant="outline" size="sm" disabled={isLoading} onClick={loadMore}>
          {isLoading ? 'Loading…' : 'Load more transactions'}
        </Button>
      )}
    </div>
  )
}
